import { memo, useMemo } from 'react';

import { Loader } from '@/components/Loader';
import type { DropdownOption } from '@/types/dropdown.types';
import { useStorage } from '@/utils/storage/useStorage.util';

import {
  GraphExplorerDropdown,
  GraphExplorerDropdownProps,
} from './GraphExplorerDropdown';

type DropdownOptionWithTooltip = DropdownOption & {
  tooltip?: string;
};

type GroupOption = DropdownOptionWithTooltip & {
  isGroup?: boolean;
  items?: DropdownOptionWithTooltip[];
};

/**
 * Helper function to find an option by value in a nested options structure.
 * Defined outside component to avoid React Compiler issues with recursive useCallback.
 */
function findOptionByValue(
  value: string,
  opts: GroupOption[]
): DropdownOptionWithTooltip | undefined {
  for (const opt of opts) {
    if (opt.value === value) return opt;
    if (opt.isGroup && opt.items) {
      const found = findOptionByValue(value, opt.items);
      if (found) return found;
    }
  }
  return undefined;
}

const GraphExplorerMultiSelectDropdownComponent = ({
  isLoading,
  label,
  options,
  selectedValues,
  multiSelect = true,
  isSearchable = true,
  isPraetorianUser = false,
  onApply,
  onClear,
  searchValue: parentSearchValue,
  setSearchValue: parentSetSearchValue,
  isLoadingOptions,
  onDropdownStateChange,
  anchorRef,
  ...restProps
}: Omit<GraphExplorerDropdownProps, 'selected' | 'onApply'> & {
  isLoading: boolean;
  label: string;
  selectedValues: string[];
  isSearchable?: boolean;
  isPraetorianUser?: boolean;
  onApply?: (selected: DropdownOptionWithTooltip[]) => void;
  onClear?: () => void;
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  isLoadingOptions?: boolean;
  onDropdownStateChange?: (isOpen: boolean, id: string) => void;
  anchorRef?: import('react').RefObject<HTMLDivElement | null>;
}) => {
  // Use external search state if provided, otherwise use internal state
  const [searchValue, setSearchValue] = useStorage(
    {
      parentState: parentSearchValue,
      onParentStateChange: parentSetSearchValue,
    },
    parentSearchValue ?? ''
  );

  // Create a stable reference for options to avoid circular reference issues
  // Using options.length and a manual deep check for stability
  const optionsRef = useMemo(() => options, [options]);

  // Create enhanced options that include both current options and any previously selected options
  const enhancedOptions = useMemo(() => {
    const selectedOptionsNotInCurrent: DropdownOptionWithTooltip[] = [];

    // Find selected values that don't exist in current options
    for (const value of selectedValues) {
      const existsInCurrent = findOptionByValue(
        value,
        optionsRef as GroupOption[]
      );
      if (!existsInCurrent) {
        selectedOptionsNotInCurrent.push({
          id: value,
          label: value,
          value: value,
        });
      }
    }

    // Combine current options with any missing selected options
    return [...optionsRef, ...selectedOptionsNotInCurrent] as GroupOption[];
  }, [optionsRef, selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return enhancedOptions;

    // Create a set of selected values for quick lookup
    const selectedValuesSet = new Set(selectedValues);

    return enhancedOptions
      .map(option => {
        if (option.isGroup && option.items) {
          // Filter items in the group, but keep selected items regardless of search
          const filteredItems = option.items.filter(
            item =>
              item.label.toLowerCase().includes(searchValue.toLowerCase()) ||
              (item.value && selectedValuesSet.has(item.value.toString()))
          );

          // Return group with filtered items
          return {
            ...option,
            items: filteredItems,
          };
        }
        // For non-group options, just check if they match the search
        return option;
      })
      .filter(option => {
        if (option.isGroup && option.items) {
          // Keep group if it has matching items or its own label matches
          return (
            option.items.length > 0 ||
            option.label.toLowerCase().includes(searchValue.toLowerCase())
          );
        }
        // Keep non-group option if its label matches, it's selected, or it's a previously selected option
        return (
          option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          (option.value && selectedValuesSet.has(option.value.toString()))
        );
      });
  }, [enhancedOptions, searchValue, selectedValues]);

  const selected = useMemo(() => {
    const selectedOptions: DropdownOptionWithTooltip[] = [];

    // Deduplicate selectedValues to avoid duplicate options
    const uniqueSelectedValues = [...new Set(selectedValues)];

    // Process each selected value - now all selected values should be found in enhancedOptions
    for (const value of uniqueSelectedValues) {
      const option = findOptionByValue(value, enhancedOptions);
      if (option) {
        selectedOptions.push(option);
      }
    }

    return selectedOptions;
  }, [enhancedOptions, selectedValues]);

  return (
    <div className="relative">
      <Loader
        className="absolute top-0 left-0 z-10 size-full rounded-2xl"
        isLoading={isLoading}
      />
      <GraphExplorerDropdown
        size="xs"
        {...restProps}
        selected={multiSelect ? selected : selected[0]}
        label={label}
        options={filteredOptions}
        active={selected.length > 0}
        multiSelect={multiSelect}
        onApply={onApply}
        onClear={onClear}
        isPraetorianUser={isPraetorianUser}
        {...(isSearchable
          ? {
              searchValue,
              onSearchChange: setSearchValue,
              searchPlaceholder: `Search ${label}...`,
            }
          : {})}
        isLoadingOptions={isLoadingOptions}
        title={`Select ${label}`}
        virtualized={filteredOptions.length > 10}
        onDropdownStateChange={onDropdownStateChange}
        anchorRef={anchorRef}
      />
    </div>
  );
};

/**
 * Memoized GraphExplorerMultiSelectDropdown to prevent unnecessary re-renders
 */
export const GraphExplorerMultiSelectDropdown = memo(
  GraphExplorerMultiSelectDropdownComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.label === nextProps.label &&
      prevProps.options.length === nextProps.options.length &&
      prevProps.selectedValues.length === nextProps.selectedValues.length &&
      prevProps.multiSelect === nextProps.multiSelect &&
      prevProps.isSearchable === nextProps.isSearchable &&
      prevProps.isPraetorianUser === nextProps.isPraetorianUser &&
      prevProps.isLoadingOptions === nextProps.isLoadingOptions &&
      prevProps.onApply === nextProps.onApply &&
      prevProps.onClear === nextProps.onClear &&
      prevProps.onDropdownStateChange === nextProps.onDropdownStateChange
    );
  }
);
